import React, { useEffect, useRef, useState } from 'react';
import { Camera, X, RefreshCw, Loader2 } from 'lucide-react';

/**
 * Captura uma foto pela câmera frontal (atribuição do apontamento, sem login).
 * Chama onCapture(blob) ao confirmar.
 */
const CameraCaptureModal = ({ open, title, onCapture, onCancel, busy, erroExterno }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [erroCam, setErroCam] = useState('');
  const [erroLocal, setErroLocal] = useState('');
  const [pronta, setPronta] = useState(false);
  const [capturando, setCapturando] = useState(false);

  useEffect(() => {
    let cancelado = false;
    async function iniciar() {
      setErroCam('');
      setErroLocal('');
      setPronta(false);
      setCapturando(false);
      try {
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'user' } },
            audio: false,
          });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
        }
        if (cancelado) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setPronta(true);
      } catch (e) {
        setErroCam('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
      }
    }
    if (open) iniciar();
    return () => {
      cancelado = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    if (erroExterno) setErroLocal('');
  }, [erroExterno]);

  if (!open) return null;

  const blobDeCanvas = (canvas) =>
    new Promise((resolve) => {
      canvas.toBlob(
        (b) => {
          if (b && b.size > 0) {
            resolve(b);
            return;
          }
          try {
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
            fetch(dataUrl)
              .then((r) => r.blob())
              .then(resolve)
              .catch(() => resolve(null));
          } catch {
            resolve(null);
          }
        },
        'image/jpeg',
        0.85
      );
    });

  const capturar = async () => {
    const video = videoRef.current;
    if (!video || !pronta || busy || capturando) return;
    setErroLocal('');
    setCapturando(true);
    try {
      // Aguarda o vídeo ter frames reais (evita canvas preto / blob vazio em tablets)
      let w = video.videoWidth;
      let h = video.videoHeight;
      if (!w || !h) {
        await new Promise((r) => setTimeout(r, 250));
        w = video.videoWidth;
        h = video.videoHeight;
      }
      if (!w || !h) {
        throw new Error('A câmera ainda não está pronta. Aguarde a imagem e tente novamente.');
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Não foi possível preparar a captura da foto.');
      // Espelha como o preview (scaleX(-1)) para a foto bater com o que o operador vê
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, w, h);

      const blob = await blobDeCanvas(canvas);
      if (!blob || blob.size < 32) {
        throw new Error('Falha ao gerar a foto. Tente novamente.');
      }
      await Promise.resolve(onCapture(blob));
    } catch (e) {
      setErroLocal(e?.message || 'Erro ao capturar a foto.');
    } finally {
      setCapturando(false);
    }
  };

  const ocupado = busy || capturando;
  const erroMostrar = erroExterno || erroLocal || erroCam;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Camera className="h-5 w-5 text-emerald-600" />
            {title || 'Identifique-se para registrar'}
          </h3>
          <button onClick={onCancel} disabled={ocupado} className="rounded-lg p-1 hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="bg-black">
          {erroCam ? (
            <div className="flex h-64 items-center justify-center px-6 text-center text-sm text-red-200">
              {erroCam}
            </div>
          ) : (
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className="h-72 w-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
          )}
        </div>

        {erroMostrar && !erroCam && (
          <div className="border-b border-red-200 bg-red-50 px-5 py-3 text-sm text-red-800">
            {erroMostrar}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <p className="text-xs text-gray-500">
            A foto registra quem fez o apontamento. Olhe para a câmera e toque em capturar.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={ocupado}
              className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={capturar}
              disabled={!pronta || ocupado || !!erroCam}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              {ocupado ? 'Registrando...' : 'Capturar e confirmar'}
            </button>
          </div>
        </div>
        {!pronta && !erroCam && (
          <div className="flex items-center justify-center gap-2 border-t py-2 text-xs text-gray-400">
            <RefreshCw className="h-3 w-3 animate-spin" /> Iniciando câmera...
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraCaptureModal;
